import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { UserCog, Eye, PenLine, Trash2, DownloadCloud, XCircle, ShieldAlert, Mail, Clock } from 'lucide-react';

const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};

export function DataRightsPage() {
    const { t } = useTranslation();

    const sections = [
        { key: 'access', icon: Eye },
        { key: 'rectification', icon: PenLine },
        { key: 'deletion', icon: Trash2 },
        { key: 'portability', icon: DownloadCloud },
        { key: 'revocation', icon: XCircle },
        { key: 'opposition', icon: ShieldAlert }
    ];

    return (
        <article className="pb-10">
            {/* Header */}
            <header className="mb-14 text-center">
                <div className="inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-full bg-brand-500/10 border border-brand-500/20 text-brand-700 dark:text-brand-300 text-sm font-medium mb-6">
                    <UserCog size={16} />
                    <span>{t('legal.badge', 'Conformidade Legal')}</span>
                </div>
                <h1>{t('legal.dataRights.title')}</h1>
                <p className="text-muted-foreground text-lg sm:text-[1.1rem] leading-relaxed mt-6 text-justify">
                    {t('legal.dataRights.introduction')}
                </p>
                <div className="mt-8 flex items-center justify-center gap-2 text-sm text-muted-foreground/80 bg-background/50 inline-flex px-4 py-2 rounded-lg border border-border/50">
                    <Clock size={16} />
                    <span className='text-left'>{t('legal.dataRights.lastUpdated')}</span>
                </div>
            </header>

            {/* Sections */}
            <div className="space-y-16 mt-16">
                {sections.map(({ key, icon: Icon }) => (
                    <motion.section
                        key={key}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-100px" }}
                        variants={fadeInUp}
                        className="scroll-mt-32 relative"
                    >
                        <div className="flex items-center gap-4 mb-6">
                            <div className="w-12 h-12 flex-shrink-0 rounded-2xl bg-gradient-to-br from-brand-500/10 to-transparent flex items-center justify-center text-brand-600 dark:text-brand-400 border border-brand-500/20 shadow-sm">
                                <Icon size={22} strokeWidth={1.5} />
                            </div>
                            <h2 className="!my-0 !border-none !pb-0 flex-1 text-2xl sm:text-[1.65rem] leading-tight mt-1.5 whitespace-pre-line sm:whitespace-normal">{t(`legal.dataRights.sections.${key}.title`)}</h2>
                        </div>
                        <div
                            className="prose-p:mt-0 pt-2 prose-p:text-justify text-justify text-muted-foreground text-[1.05rem] leading-relaxed pl-0 md:pl-16 whitespace-pre-line"
                            dangerouslySetInnerHTML={{
                                __html: t(`legal.dataRights.sections.${key}.content`).replace(/\*\*(.*?)\*\*/g, '<strong class="text-red-500 font-semibold dark:text-red-400">$1</strong>')
                            }}
                        />
                    </motion.section>
                ))}
            </div>

            {/* Contact Footer Banner (Specific for DPO) */}
            {/* Contact Footer Banner (Specific for DPO) */}
            <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                className="mt-20 p-8 sm:p-10 lg:p-14 rounded-[2.5rem] bg-card/50 dark:bg-[#1C1714]/80 backdrop-blur-sm border border-border/60 shadow-sm flex flex-col gap-10 lg:gap-12 relative overflow-hidden group"
            >
                {/* Decorative background glow */}
                <div className="absolute top-0 right-0 w-[500px] h-[500px] -mr-32 -mt-32 rounded-full bg-brand-500/10 blur-[100px] pointer-events-none" />
                <div className="absolute bottom-0 left-0 w-[500px] h-[500px] -ml-32 -mb-32 rounded-full bg-brand-500/10 blur-[100px] pointer-events-none" />

                <div className="relative z-10 w-full gap-4 flex flex-col">
                    <h3 className="!my-0 text-3xl sm:text-4xl font-display font-semibold mb-6 text-foreground leading-tight">
                        {t('legal.dataRights.form.title')}
                    </h3>

                    <p className="text-muted-foreground w-full !my-0 text-[1.15rem] sm:text-xl mb-10 leading-relaxed md:max-w-5xl">
                        {t('legal.dataRights.form.description')}
                    </p>

                    {/* Timeframes Grid (Compact & Premium MUI Style) */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-2 w-full">
                        {/* Card: Brasil */}
                        <div className="bg-background/90 dark:bg-background/40 backdrop-blur-md border border-brand-500/20 rounded-2xl p-4 sm:p-5 shadow-sm flex flex-row sm:flex-col items-center sm:items-start justify-between sm:justify-center gap-2 sm:gap-3 transition-colors hover:bg-brand-500/5 group/card">
                            <span className="text-[0.65rem] sm:text-xs font-bold text-brand-600 dark:text-brand-400 bg-brand-500/10 px-2.5 py-1 rounded-md tracking-wider uppercase flex items-center gap-1.5 shrink-0">
                                Brasil <span className="opacity-70 font-medium">(LGPD)</span>
                            </span>
                            <span className="text-base sm:text-lg font-semibold text-foreground tracking-tight group-hover/card:text-brand-700 dark:group-hover/card:text-brand-300 transition-colors">
                                {t('legal.dataRights.form.timeframes.br')}
                            </span>
                        </div>

                        {/* Card: Europa */}
                        <div className="bg-background/90 dark:bg-background/40 backdrop-blur-md border border-brand-500/20 rounded-2xl p-4 sm:p-5 shadow-sm flex flex-row sm:flex-col items-center sm:items-start justify-between sm:justify-center gap-2 sm:gap-3 transition-colors hover:bg-brand-500/5 group/card">
                            <span className="text-[0.65rem] sm:text-xs font-bold text-brand-600 dark:text-brand-400 bg-brand-500/10 px-2.5 py-1 rounded-md tracking-wider uppercase flex items-center gap-1.5 shrink-0">
                                Europa <span className="opacity-70 font-medium">(GDPR)</span>
                            </span>
                            <span className="text-base sm:text-lg font-semibold text-foreground tracking-tight group-hover/card:text-brand-700 dark:group-hover/card:text-brand-300 transition-colors">
                                {t('legal.dataRights.form.timeframes.eu')}
                            </span>
                        </div>

                        {/* Card: EUA */}
                        <div className="bg-background/90 dark:bg-background/40 backdrop-blur-md border border-brand-500/20 rounded-2xl p-4 sm:p-5 shadow-sm flex flex-row sm:flex-col items-center sm:items-start justify-between sm:justify-center gap-2 sm:gap-3 transition-colors hover:bg-brand-500/5 group/card">
                            <span className="text-[0.65rem] sm:text-xs font-bold text-brand-600 dark:text-brand-400 bg-brand-500/10 px-2.5 py-1 rounded-md tracking-wider uppercase flex items-center gap-1.5 shrink-0">
                                EUA <span className="opacity-70 font-medium">(CCPA)</span>
                            </span>
                            <span className="text-base sm:text-lg font-semibold text-foreground tracking-tight group-hover/card:text-brand-700 dark:group-hover/card:text-brand-300 transition-colors">
                                {t('legal.dataRights.form.timeframes.us')}
                            </span>
                        </div>
                    </div>

                    <p className="text-base text-muted-foreground/80 italic !my-0 max-w-4xl leading-relaxed">
                        {t('legal.dataRights.form.note')}
                    </p>
                </div>

                {/* Button Area */}
                <div className="relative z-10 w-full flex items-center justify-center mt-2">
                    <a
                        href="mailto:dpo@coffeeback.com"
                        className="!text-white !no-underline transition-all duration-300 bg-[#6F4E37] hover:bg-[#5A3E2A] px-10 py-5 rounded-2xl font-semibold shadow-md flex items-center justify-center gap-3 whitespace-nowrap w-full sm:w-auto active:scale-[0.98] ring-1 ring-[#6F4E37]/30 text-lg group"
                    >
                        <Mail size={22} className="transition-transform group-hover:-translate-y-0.5" />
                        {t('legal.privacy.contact.button', 'Enviar Solicitação')}
                    </a>
                </div>
            </motion.div>
        </article>
    );
}
